import type {InputWrapperProps} from '@mantine/core';
import {Input, Select, Stack, Text} from '@mantine/core';
import type {FC} from 'react';
import type {ISelectOption} from 'src/types/Form';

/**
 * Labeled single-select dropdown rendered inside an `Input.Wrapper`. `role="combobox"` is
 * restored explicitly since Mantine's `Select` renders a plain `<input>` with no explicit role.
 * @registry Labeled select for choosing a single item from a fixed list; an option's `description` shows as a dimmed subline.
 */
export interface SelectBoxProps {
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  description?: string;
  withAsterisk?: boolean;
  options: ISelectOption[];
  value?: string;

  wrapperProps?: InputWrapperProps;
  /** Set false inside a Popover, whose click-outside check closes on a portaled dropdown. */
  withinPortal?: boolean;

  onSelectChangeCallback: (value: string) => void;
}
export const SelectBox: FC<SelectBoxProps> = ({
  label,
  ariaLabel,
  placeholder,
  name,
  disabled,
  error,
  options,
  value,
  onSelectChangeCallback,
  description,
  withAsterisk,
  wrapperProps,
  withinPortal = true,
}) => {
  const data = options.map(option => ({
    value: String(option.id),
    label: option.text ?? option.label,
  }));
  const descriptions = new Map(
    options
      .filter(option => option.description)
      .map(option => [String(option.id), option.description])
  );

  return (
    <Input.Wrapper
      mb="md"
      description={description}
      error={error && 'This field is required'}
      label={label}
      withAsterisk={withAsterisk}
      {...wrapperProps}>
      <Select
        name={name}
        disabled={disabled}
        value={value ?? null}
        error={error}
        data={data}
        placeholder={placeholder}
        aria-label={ariaLabel}
        clearable
        comboboxProps={{withinPortal, zIndex: 1001}}
        nothingFoundMessage="No options available"
        onChange={val => onSelectChangeCallback(val ?? '')}
        renderOption={
          descriptions.size > 0
            ? ({option}) => (
                <Stack gap={0}>
                  <Text size="sm">{option.label}</Text>
                  <Text size="xs" c="dimmed">
                    {descriptions.get(option.value)}
                  </Text>
                </Stack>
              )
            : undefined
        }
        role="combobox"
      />
    </Input.Wrapper>
  );
};
